// Libs
import clsx from "clsx";
import React from "react";

import type { RTableCellProps } from "../../core/utils";

import type { TableBorderMode } from "../../organisms/table";

export interface CellProps extends React.PropsWithChildren<RTableCellProps> {
    /**
     * Align content
     */
    align?: "left" | "center" | "right";
    /**
     * Custom cell component
     */
    CellComponent?: React.ElementType;
    /**
     * Additional className
     */
    className?: string;
    /**
     * Additional container className
     */
    containerClassName?: string;
    /**
     * If is disabled
     */
    disabled?: boolean;
    /**
     * Formatter to value
     */
    formatter?: (child: React.ReactNode) => React.ReactNode;
    /**
     * If is Hovered
     */
    hover?: boolean;
    /**
     * If no spacing is applied
     */
    noSpacing?: boolean;
    /**
     * Hover handler
     */
    onHover?: (hover: boolean) => void;
    /**
     * check if the first left column static
     */
    isStatic?: boolean;
    /**
     * Border bottom
     */
    borderBottom?: boolean;
    /**
     * bordered
     */
    borderMode?: TableBorderMode;
    /**
     * apply right border (used for grouped border mode)
     */
    hasRightBorder?: boolean;
    /**
     * index of row and column
     */
    indexRow?: number;
    /**
     *  index of column
     */
    indexColumn?: number;
}

export const Cell = React.memo(
    React.forwardRef<HTMLTableCellElement, CellProps>(function Cell(
        {
            align = "left",
            CellComponent = "td",
            children,
            className,
            containerClassName: containerClassNameProp,
            formatter,
            noSpacing = false,
            isStatic = false,
            borderBottom = false,
            borderMode = "none",
            hasRightBorder = false,
            indexRow,
            indexColumn,
            ...otherProps
        }: CellProps,
        ref
    ) {
        const rootClassName = clsx(
            "ds-c-cell",
            `ds-c-cell--${align}`,
            {
                "ds-c-cell--spaced": !noSpacing,
                "ds-c-cell--bordered-bottom": borderBottom,
                "ds-c-cell--bordered": borderMode === "all",
                "ds-c-cell--bordered-right": borderMode === "group" && hasRightBorder,
                "ds-c-cell--static": isStatic,
            },
            className
        );
        const containerClassName = clsx(
            "ds-c-cell__container",
            "ds-u-typography--body",
            containerClassNameProp
        );

        let child = children;
        if (formatter) {
            child = formatter(child);
        } else if (typeof children === "undefined" || children === null) {
            child = "";
        }

        return (
            <CellComponent
                ref={ref}
                key={`${indexRow}-${indexColumn}-table`}
                className={rootClassName}
                {...otherProps}
            >
                <div className={containerClassName}>{child}</div>
            </CellComponent>
        );
    })
);
